import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button, { ButtonTypes } from "../../../common/components/button/button";
import WebLayout from "../../../common/components/layouts/webLayout/webLayout";
import Table from "../../../common/components/table/table";
import {
  useErrorHandler,
  useLoadHandler,
} from "../../../common/customHooks/useLoadHandler";
import { campaignTableColumns } from "../constants/campaignTableColumns";
import { ReactComponent as EditIcon } from "../../../assets/editIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trashIcon.svg";
import "./homepage.scss";
import { CampaignApi } from "../../../common/services/api/campaignApi";
import { CampaignsListModel } from "../models/campaignsListModel";
import Toggle from "../../../common/components/toggle/toggle";
import ConfirmationPopup from "../../../common/components/confirmationPopup/confirmationPopup";
import { ReactComponent as EmptyCampaign } from "../../../assets/emptyCampaignImage.svg";

export default function HomePage() {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedCampaign, setSelectedCampaign] =
    useState<CampaignsListModel>();
  const [rows, setRows] = useState<
    {
      key: string;
      value: any;
    }[][]
  >();

  useEffect(() => {
    callFetchAsync();
  }, []);

  const { loadingStatus, callFetchAsync } = useLoadHandler(async () => {
    const result = await getCampaignRows();
    return setRows(result);
  });

  const { callFetchAsync: toggleCallFetchAsync } = useErrorHandler(
    async (id: string) => {
      await CampaignApi().toggleCampaignActive(id);
      return callFetchAsync();
    }
  );

  const {
    loadingStatus: deleteLoadingStatus,
    callFetchAsync: deleteCallFetchAsync,
  } = useLoadHandler(async (id: string) => {
    await CampaignApi().deleteCampaign(id);
    return callFetchAsync();
  });

  async function getCampaignRows() {
    let result = ((await CampaignApi().campaigns()) ??
      []) as CampaignsListModel[];
    return result.map((e) => [
      { key: "name", value: e.name },
      {
        key: "created",
        value: e.createAt && new Date(e.createAt).toLocaleString(),
      },
      {
        key: "lastUpadted",
        value: e.lastUpdate && new Date(e.lastUpdate).toLocaleString(),
      },
      {
        key: "active",
        value: (
          <Toggle
            isOn={e.isActive}
            onChange={() => toggleCallFetchAsync(e.id)}
          />
        ),
      },
      {
        key: "actions",
        value: (
          <div className="home-actions-container">
            <Button
              type={ButtonTypes.PRIMARY}
              text={"View Result"}
              isDense
              onClick={() => navigate(`/campaign-result?id=${e.id}`)}
            />
            <EditIcon
              style={{ cursor: "pointer" }}
              stroke={"black"}
              height={20}
              width={20}
              onClick={() => navigate(`/edit-campaign?index=0&id=${e.id}`)}
            />
            <TrashIcon
              style={{ cursor: "pointer" }}
              height={20}
              width={20}
              stroke={"red"}
              onClick={() => {
                setSelectedCampaign(e);
                setShowConfirmation(true);
              }}
            />
          </div>
        ),
      },
    ]);
  }

  return (
    <>
      <WebLayout pageIndex={0}>
        <div className="head">
          <div>
            <h3 style={{ margin: 0 }}>Techsauce</h3>
            <p style={{ fontSize: "12px" }}>Campaign Management System</p>
          </div>
          <Button
            className="home-create-button"
            type={ButtonTypes.PRIMARY}
            text="+ Create"
            onClick={() => navigate(`/create-campaign?index=0`)}
          />
        </div>
        <Table
          columns={campaignTableColumns}
          loadingStatus={loadingStatus}
          rows={rows}
          emptySpace={
            <EmptyCampaign
              height={250}
              width={250}
              style={{ paddingBottom: "2rem" }}
            />
          }
        />
      </WebLayout>
      {showConfirmation && (
        <ConfirmationPopup
          description={`Are you sure you want to delete campaign "${selectedCampaign?.name}"`}
          loadingStatus={deleteLoadingStatus}
          onConfirm={async () => {
            await deleteCallFetchAsync(selectedCampaign?.id);
            setShowConfirmation(false);
          }}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </>
  );
}
